import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, Lock, Loader2, Globe, Shield, CheckCircle } from 'lucide-react';
import apiService from '../services/api';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showLangMenu, setShowLangMenu] = useState(false);
  const isRTL = i18n.language === 'ar';

  useEffect(() => {
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = i18n.language;
  }, [i18n.language, isRTL]);

  // Redirect to dashboard if already logged in
  useEffect(() => {
    if (apiService.isAuthenticated()) {
      const user = apiService.getCurrentUser();
      const userType = user?.userType;

      if (userType === 'admin') {
        navigate('/dashboard', { replace: true });
      } else if (userType === 'company_owner' || userType === 'company_manager') {
        navigate('/company-dashboard', { replace: true });
      } else {
        navigate('/complaints', { replace: true });
      }
    }
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await apiService.login(email, password);
      const userType = response.user.userType;

      if (userType === 'evaluator') {
        // Evaluators are not allowed to use this web portal
        apiService.logout();
        setError(t('auth.evaluatorNotAllowed'));
        return;
      }

      if (userType === 'admin') {
        navigate('/dashboard');
      } else if (userType === 'company_owner') {
        navigate('/company-dashboard');
      } else if (userType === 'company_manager') {
        navigate('/company-dashboard');
      } else {
        navigate('/complaints');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || t('auth.loginError'));
    } finally {
      setLoading(false);
    }
  };

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    localStorage.setItem('language', lng);
    document.documentElement.dir = lng === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = lng;
    setShowLangMenu(false);
  };

  const languages = [
    { code: 'ar', name: 'العربية', flag: '🇸🇦' },
    { code: 'en', name: 'English', flag: '🇬🇧' },
  ];

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-brand-50 via-white to-emerald-50 relative overflow-hidden p-4">
      {/* Animated Background Decorations */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-0 w-96 h-96 bg-brand-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse"></div>
        <div className="absolute top-20 right-20 w-80 h-80 bg-emerald-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse" style={{ animationDelay: '2s' }}></div>
        <div className="absolute bottom-20 left-40 w-72 h-72 bg-green-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse" style={{ animationDelay: '4s' }}></div>

        {/* Grid Pattern */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0iIzE2YTM0YSIgc3Ryb2tlLW9wYWNpdHk9IjAuMDUiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] opacity-40"></div>
      </div>

      {/* Language Switcher */}
      <div className={`absolute top-6 z-20 ${isRTL ? 'left-6' : 'right-6'}`}>
        <div className="relative">
          <button
            onClick={() => setShowLangMenu(!showLangMenu)}
            className="flex items-center gap-2 px-4 py-2.5 bg-white/80 backdrop-blur-sm border-2 border-brand-200 hover:border-brand-400 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300"
          >
            <Globe className="w-5 h-5 text-brand-700" />
            <span className="font-semibold text-gray-700">{currentLanguage.flag} {currentLanguage.name}</span>
          </button>

          {showLangMenu && (
            <div className="absolute top-full mt-2 right-0 bg-white rounded-2xl shadow-2xl border border-gray-100 py-2 min-w-[180px] z-30 animate-fadeIn">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => changeLanguage(lang.code)}
                  className={`w-full px-4 py-2.5 text-left hover:bg-brand-50 transition-colors font-medium flex items-center gap-3 ${
                    i18n.language === lang.code ? 'bg-brand-50 text-brand-700' : 'text-gray-700'
                  }`}
                >
                  <span className="text-xl">{lang.flag}</span>
                  <span>{lang.name}</span>
                  {i18n.language === lang.code && <CheckCircle className="w-4 h-4 ml-auto text-brand-600" />}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Login Card */}
      <div className="w-full max-w-md relative z-10 animate-fadeIn">
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-100 overflow-hidden">
          {/* Header with Gradient */}
          <div className="relative overflow-hidden bg-gradient-to-r from-brand-600 via-brand-700 to-emerald-700 px-8 py-10">
            <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-brand-800/20 rounded-full -ml-12 -mb-12"></div>

            <div className="relative text-center">
              {/* Logo */}
              <div className="mx-auto w-20 h-20 rounded-3xl bg-white/20 backdrop-blur-sm flex items-center justify-center shadow-2xl mb-4 border-4 border-white/30">
                <Shield className="w-10 h-10 text-white" />
              </div>

              {/* Title */}
              <h1 className="text-3xl font-black text-white mb-2">
                {t('app.title')}
              </h1>
              <p className="text-brand-50 text-lg font-medium">
                {t('app.subtitle')}
              </p>
            </div>
          </div>

          {/* Form Content */}
          <div className="p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Error Message */}
              {error && (
                <div className="bg-red-50 border-l-4 border-red-500 rounded-xl p-4 animate-fadeIn">
                  <p className="text-red-800 font-medium text-sm">{error}</p>
                </div>
              )}

              {/* Email Input */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2" dir={isRTL ? 'rtl' : 'ltr'}>
                  {t('auth.email')}
                </label>
                <div className="relative">
                  <Mail className={`absolute top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 ${isRTL ? 'right-4' : 'left-4'}`} />
                  <input
                    id="email"
                    type="email"
                    placeholder={t('auth.email')}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={loading}
                    dir={isRTL ? 'rtl' : 'ltr'}
                    className={`w-full ${isRTL ? 'pr-12 pl-4' : 'pl-12 pr-4'} py-3.5 bg-gray-50 border-2 border-gray-200 rounded-2xl focus:outline-none focus:border-brand-500 focus:bg-white transition-all duration-300 font-medium disabled:opacity-50 disabled:cursor-not-allowed`}
                  />
                </div>
              </div>

              {/* Password Input */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-bold text-gray-700" dir={isRTL ? 'rtl' : 'ltr'}>
                    {t('auth.password')}
                  </label>
                  <button
                    type="button"
                    onClick={() => navigate('/forgot-password')}
                    className="text-sm text-brand-600 hover:text-brand-700 font-semibold transition-colors"
                    dir={isRTL ? 'rtl' : 'ltr'}
                  >
                    {isRTL ? 'نسيت كلمة المرور؟' : 'Forgot Password?'}
                  </button>
                </div>
                <div className="relative">
                  <Lock className={`absolute top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 ${isRTL ? 'right-4' : 'left-4'}`} />
                  <input
                    id="password"
                    type="password"
                    placeholder={t('auth.password')}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={loading}
                    dir={isRTL ? 'rtl' : 'ltr'}
                    className={`w-full ${isRTL ? 'pr-12 pl-4' : 'pl-12 pr-4'} py-3.5 bg-gray-50 border-2 border-gray-200 rounded-2xl focus:outline-none focus:border-brand-500 focus:bg-white transition-all duration-300 font-medium disabled:opacity-50 disabled:cursor-not-allowed`}
                  />
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full py-4 bg-gradient-to-r from-brand-600 to-brand-700 hover:from-brand-700 hover:to-brand-800 text-white font-bold rounded-2xl transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>{t('common.loading')}</span>
                  </>
                ) : (
                  <span>{t('auth.loginButton')}</span>
                )}
              </button>
            </form>

            {/* Security Notice */}
            <div className="mt-6 p-4 bg-gradient-to-r from-brand-50 to-emerald-50 rounded-2xl border border-brand-100">
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-brand-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-gray-700" dir={isRTL ? 'rtl' : 'ltr'}>
                  {isRTL
                    ? 'نظام آمن ومشفّر لحماية بياناتك'
                    : 'Secure and encrypted system to protect your data'}
                </p>
              </div>
            </div>
          </div>

          {/* Bottom Accent */}
          <div className="h-2 bg-gradient-to-r from-brand-500 via-brand-600 to-emerald-600"></div>
        </div>

        {/* Features */}
        <div className="mt-8 grid grid-cols-3 gap-4">
          <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-4 text-center border border-white/80 shadow-lg">
            <div className="w-10 h-10 mx-auto mb-2 bg-brand-100 rounded-xl flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-brand-600" />
            </div>
            <p className="text-xs font-bold text-gray-700" dir={isRTL ? 'rtl' : 'ltr'}>
              {isRTL ? 'آمن 100%' : '100% Secure'}
            </p>
          </div>
          <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-4 text-center border border-white/80 shadow-lg">
            <div className="w-10 h-10 mx-auto mb-2 bg-emerald-100 rounded-xl flex items-center justify-center">
              <Shield className="w-5 h-5 text-emerald-600" />
            </div>
            <p className="text-xs font-bold text-gray-700" dir={isRTL ? 'rtl' : 'ltr'}>
              {isRTL ? 'محمي' : 'Protected'}
            </p>
          </div>
          <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-4 text-center border border-white/80 shadow-lg">
            <div className="w-10 h-10 mx-auto mb-2 bg-green-100 rounded-xl flex items-center justify-center">
              <Lock className="w-5 h-5 text-green-600" />
            </div>
            <p className="text-xs font-bold text-gray-700" dir={isRTL ? 'rtl' : 'ltr'}>
              {isRTL ? 'مشفّر' : 'Encrypted'}
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="absolute bottom-6 left-0 right-0 text-center text-sm text-gray-500 font-medium">
        <p>© 2026 {t('app.title')} - v1.0.0</p>
      </div>
    </div>
  );
};

export default LoginPage;
